import type { FormFieldDeclaration } from "./types";

/**
 * Делает поверхностную копию default-значения, чтобы избежать shared-mutation для `array`/`object`.
 * @template T тип значения
 * @param value исходное default-значение
 * @returns склонированное или исходное значение
 */
function cloneDefaultValue<T>(value: T): T {
    if (Array.isArray(value)) {
        return [...value] as T;
    }
    if (value != null && typeof value === "object") {
        return { ...(value as Record<string, unknown>) } as T;
    }
    return value;
}

/**
 * Возвращает default-значение поля по декларации.
 * Приоритет:
 * 1) `declaration.default` (если задан);
 * 2) `null`, если `nullable === true`;
 * 3) fallback по `type`.
 * @template TForm тип значений формы
 * @template TDomain тип доменной модели
 * @template TFieldKey имя поля
 * @param declaration декларация поля формы
 * @returns default-значение для этого поля
 */
export function getDefaultValueByDeclaration<TForm extends object, TDomain, TFieldKey extends keyof TForm, TContext = void>(
    declaration: FormFieldDeclaration<TForm, TDomain, TFieldKey, TContext>,
): TForm[TFieldKey] {
    if ("default" in declaration && declaration.default !== undefined) {
        return cloneDefaultValue(declaration.default);
    }

    if (declaration.nullable === true) {
        return null as TForm[TFieldKey];
    }

    switch (declaration.type) {
        case "string":
            return "" as TForm[TFieldKey];
        case "number":
            return 0 as TForm[TFieldKey];
        case "boolean":
            return false as TForm[TFieldKey];
        case "array":
            return [] as TForm[TFieldKey];
        case "object":
            return {} as TForm[TFieldKey];
        case "date":
        case "nullable":
            return null as TForm[TFieldKey];
        default:
            return null as TForm[TFieldKey];
    }
}
